// src/components/GlobalTicker.jsx
// Displays owner-controlled messages in a scoreboard-style ticker at the bottom of the screen
// Only visible when there's an active message

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../utils/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const SIDEBAR_WIDTH = 250; // Match NavigationSidebar width

const GlobalTicker = () => {
  const [tickerData, setTickerData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const positionRef = useRef(0);

  useEffect(() => {
    // Listen to the ticker document in real-time
    const tickerDocRef = doc(db, 'system', 'ticker');
    
    const unsubscribe = onSnapshot(tickerDocRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        console.log('🎯 Ticker data received:', data);
        
        if (data.isActive && data.message && data.message.trim() !== '') {
          setTickerData(data);
          setIsVisible(true);
          setIsDismissed(false);
        } else {
          setIsVisible(false);
          setTickerData(null);
        }
      } else {
        setIsVisible(false);
        setTickerData(null);
      }
    }, (error) => {
      console.error('Error listening to ticker:', error);
    });

    return () => unsubscribe();
  }, []);

  // Manual animation loop for precise control
  const animate = useCallback(() => {
    if (!scrollRef.current || !containerRef.current) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    const messageWidth = scrollRef.current.offsetWidth;
    const containerWidth = containerRef.current.offsetWidth;
    const speed = 0.8; // pixels per frame (adjust for speed)

    // Move left
    positionRef.current -= speed;

    // Reset when message has completely exited left side
    // Message starts at containerWidth (right edge) and needs to travel containerWidth + messageWidth
    if (positionRef.current < -messageWidth) {
      positionRef.current = containerWidth;
    }

    scrollRef.current.style.transform = `translateX(${positionRef.current}px)`;
    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // Start/stop animation based on visibility
  useEffect(() => {
    if (isVisible && !isDismissed && tickerData) {
      // Initialize position to start off-screen right
      if (containerRef.current) {
        positionRef.current = containerRef.current.offsetWidth;
      }
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, isDismissed, tickerData, animate]);

  // Don't render if not visible or dismissed
  if (!isVisible || isDismissed || !tickerData) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  return (
    <>
      {/* Ticker Container - Fixed to bottom, starts after sidebar */}
      <div style={styles.tickerContainer} className="global-ticker">
        {/* Scoreboard background */}
        <div style={styles.scoreboard}>
          {/* LED dot pattern overlay */}
          <div style={styles.ledOverlay} />
          
          {/* Scrolling message wrapper */}
          <div ref={containerRef} style={styles.scrollWrapper}>
            <div ref={scrollRef} style={styles.messageGroup}>
              {tickerData.showIcon && <span style={styles.icon}>📢</span>}
              <span style={styles.messageText}>{tickerData.message}</span>
            </div>
          </div>

          {/* Dismiss button */}
          {tickerData.allowDismiss !== false && (
            <button
              onClick={handleDismiss}
              style={styles.dismissButton}
              aria-label="Dismiss ticker"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Responsive styles */}
      <style>
        {`
          @keyframes tickerSlideIn {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          /* Desktop - account for 250px sidebar */
          @media (min-width: 769px) {
            .global-ticker {
              left: ${SIDEBAR_WIDTH}px !important;
            }
          }

          /* Mobile - full width */
          @media (max-width: 768px) {
            .global-ticker {
              left: 0 !important;
            }
          }
        `}
      </style>
    </>
  );
};

const styles = {
  tickerContainer: {
    position: 'fixed',
    bottom: 0,
    left: SIDEBAR_WIDTH,
    right: 0,
    zIndex: 50,
    animation: 'tickerSlideIn 0.3s ease-out',
  },
  scoreboard: {
    backgroundColor: '#0a0a0a',
    background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 50%, #000000 100%)',
    borderTop: '3px solid #333',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5), inset 0 2px 4px rgba(255, 255, 255, 0.05)',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  ledOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle, transparent 1px, #000 1px)',
    backgroundSize: '3px 3px',
    opacity: 0.3,
    pointerEvents: 'none',
  },
  scrollWrapper: {
    flex: 1,
    overflow: 'hidden',
    marginRight: '40px',
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  messageGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    whiteSpace: 'nowrap',
    position: 'absolute',
    willChange: 'transform',
  },
  icon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  messageText: {
    color: '#FFD700',
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    textShadow: '0 0 4px rgba(255, 215, 0, 0.4)',
  },
  dismissButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.5)',
    border: '1px solid #333',
    color: '#888',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'color 0.2s, background 0.2s',
    zIndex: 1,
  },
};

export default GlobalTicker;
